import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Form } from '../../../components/ui/Form';
import { emailRegx } from '../../../helper/Regx';
import { DATA_CENTERS, LOCK_PROVIDERS, ORGANIZATION_PROVIDERS } from '../../../utils/mappings';
import { Alfred, LockProviders, isICTLockProvider } from '../../../utils/lockInfo';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  StyledModalBody,
  StyledModalV2
} from '../../../components/Modals';
import { ACCEPTED_IMG_MIME_TYPES, MAX_IMG_FILE_SIZE } from '../../Deliveries/constants';
import { formatMbFileSize } from '../../../utils/utils';
import { ButtonBodyVariant } from '../../../components/ui/ButtonVariant';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../utils/colorCode';
import OrgInfoFormInputs from './OrgInfoFormInputs';
import CoreFeatureFormInputs from './CoreFeatureFormInputs';
import UserAccessFormInputs from './UserAccessFormInputs';
import FeatureModuleFormInputs from './FeatureModuleFormInputs';
import CustomSettingFormInputs from './CustomSettingFormInputs';
import {
  addOrganization,
  clearOrgError,
  editOrganization,
  getAllOrganizations,
  getOrganization,
  getOrganizations,
  setAllOrgStatus,
  uploadOrgLogo
} from '../../../redux/actions/organization';
import { setToast } from '../../../redux/actions/window';
import { searchTable, setSearchPagination, setSearchState } from '../../../redux/actions/search';
import { INTERCOM_NAME_SETTINGS, INTERCOM_UNIT_NAME_DISPLAY_SETTINGS, SEARCH_TABLES } from '../../../helper/constants';
import { setUserSearch } from '../../../redux/actions/user';
import { GET_ALL_ORGANIZATIONS, GET_ORGANIZATION } from '../../../redux/constants';
import { resetOrganizationData } from '../../../redux/actions/addOrganization';
import { requestPending, requestSuccess } from '../../../utils/status';
import { DEFAULT_USER_PIN_LENGTH } from '../../users/constant';
import { useScrollToBottom } from '../../../hooks/useScrollToBottom';

const AddEditOrganization = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const modal = useSelector((state) => state.headerModal.modal);
  const { organization, status, pagination } = useSelector(
    (state) => state.Organization
  );
  const searchState = useSelector((state) => state.search);
  const orgDefaultData = useSelector((state) => state.addOrganization);
  const isEdit = modal === 'editOrganization';

  const schema = Yup.object().shape({
    name: Yup.string()
      .max(50, t('Not more than 50 characters'))
      .required(t('Organization name is required')),
    contact: Yup.string()
      .max(50, t('Not more than 50 characters'))
      .required(t('Contact is required')),
    email: Yup.string()
      .email(t('Invalid email address'))
      .matches(emailRegx, t('Invalid Email'))
      .required(t('Email is required')),
    logo: Yup.mixed()
      .test('is-valid-type', t('Only JPG and PNG files are supported'), (value) => {
        if (!value) return true;
        return ACCEPTED_IMG_MIME_TYPES.includes(value.blob.type);
      })
      .test('is-valid-size', t('Maximum file size is', { maxFileSize: formatMbFileSize(MAX_IMG_FILE_SIZE) }), (value) => {
        if (!value) return true;
        return value.blob.size <= MAX_IMG_FILE_SIZE;
      }),
    resident_provider: Yup.mixed()
      .required(t('Resident Provider is required'))
      .oneOf(ORGANIZATION_PROVIDERS.map((v) => v.id)),
    property_provider: Yup.mixed()
      .required(t('Property Provider is required'))
      .oneOf(ORGANIZATION_PROVIDERS.map((v) => v.id)),
    service_provider: Yup.mixed()
      .required(t('Service Provider is required'))
      .oneOf(ORGANIZATION_PROVIDERS.map((v) => v.id)),
    service_request_url: Yup.string().url('Invalid URL'),
    payment_redirect_url: Yup.string().url('Invalid URL'),
    location: Yup.mixed()
      .required(t('Data Center is required'))
      .oneOf(DATA_CENTERS.map((v) => v.id)),
    lock_provider: Yup.number()
      .required(t('Lock Provider is required'))
      .oneOf(LOCK_PROVIDERS.map((v) => v.id)),
    provider_access_token: Yup.string().when('lock_provider', {
      is: (val) => !isEdit && (val === LockProviders.YALE || val === LockProviders.ICT_YALE || val === LockProviders.BRIVO_YALE),
      then: Yup.string().required(t('Yale Access Token is required')),
      otherwise: Yup.string().nullable()
    }),
    provider_refresh_token: Yup.string().when('lock_provider', {
      is: (val) => !isEdit && (val === LockProviders.YALE || val === LockProviders.ICT_YALE || val === LockProviders.BRIVO_YALE),
      then: Yup.string().required(t('Yale Refresh Token is required')),
      otherwise: Yup.string().nullable()
    }),
    house_id: Yup.string().when('lock_provider', {
      is: (val) => val === LockProviders.YALE || val === LockProviders.ICT_YALE || val === LockProviders.BRIVO_YALE,
      then: (schema) => schema.required(t('House ID is required for Yale organizations')),
      otherwise: (schema) => schema.notRequired()
    }),
    enable_posts: Yup.bool().required('Missing Enable Posts'),
    enable_offers: Yup.bool().required('Missing Enable Offers'),
    enable_booking: Yup.bool().required('Missing Enable Bookings'),
    enable_lock_screen: Yup.bool().required('Missing Enable Lock Screen'),
    enable_service_requests: Yup.bool().required('Missing Enable Service Requests'),
    show_user_pin_resident: Yup.bool().required('Missing Show User Pin Resident'),
    show_user_pin_admin: Yup.bool().required('Missing Show User Pin Admin'),
    show_user_pin_to_admin: Yup.bool().required('Missing Show User Pin to Admin'),
    enable_add_guests: Yup.bool().required('Missing Organization Enable Add Guests'),
    enable_add_co_residents: Yup.bool().required('Missing Organization Enable Add CoResidents'),
    allow_resident_credential_issuance: Yup.bool().required('Missing Allow Resident Credential Issuance'),
    org_allow_edits: Yup.bool().required('Missing Organization Allow Edit'),
    enable_thermostats: Yup.bool().required('Missing Enable Thermostats'),
    enable_leak_detectors: Yup.bool().required('Missing Enable Leak Detectors'),
    enable_deliveries: Yup.bool().required('Missing Enable Deliveries'),
    enable_chargers: Yup.bool().required('Missing Enable Chargers'),
    enable_document_storage: Yup.bool().required('Missing Enable Document Storage'),
    enable_events: Yup.bool().required('Missing Enable Events'),
    enable_v2_home_page: Yup.bool().required('Missing Enable V2 Home Page'),
    enable_chat: Yup.bool().required('Missing Enable Chat'),
    enable_akuvox: Yup.bool().required('Missing Enable Akuvox'),
    sync_akuvox_co_residents: Yup.bool().required('Missing Sync Akuvox Co-Residents'),
    enable_akuvox_private_pins: Yup.bool().required('Missing Enable Akuvox Private PINs'),
    enable_edit_intercom_pin: Yup.bool().required('Missing Enable Edit Intercom PIN'),
    intercom_name_format: Yup.string().required('Missing Intercom Name Format'),
    intercom_unit_name_display: Yup.string().required('Missing Intercom Unit Name Display'),
    enable_security_cameras: Yup.bool().required('Missing Enable Security Cameras'),
    suite_enabled: Yup.bool().required('Missing Enable Multi Lock'),
    enable_license_plates: Yup.bool().required('Missing Enable License Plates'),
    enable_vopay: Yup.bool().required('Missing Enable Vopay'),
    enable_custom_service_request_categories: Yup.bool().required('Missing Enable Custom Service Request Categories'),
    enable_mfa: Yup.bool().required('Missing Enable MFA'),
    user_pin_len: Yup.number()
      .required()
      .min(Alfred.pinMinLength, t('Minimum Pin Length is 4'))
      .max(Alfred.pinMaxLength, t('Maximum Pin Length is 10')),
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      logo: null,
      location: orgDefaultData.dataCenter,
      provider_access_token: orgDefaultData.yaleAccessToken || '',
      provider_refresh_token: orgDefaultData.yaleRefreshToken || '',
      house_id: '',
      lock_provider: orgDefaultData.options.lockProvider,
      resident_provider: orgDefaultData.residentProvider,
      property_provider: orgDefaultData.propertyProvider,
      service_provider: orgDefaultData.serviceProvider,
      enable_posts: orgDefaultData.options.enablePost,
      enable_offers: orgDefaultData.options.enableOffers,
      enable_booking: orgDefaultData.options.enableBooking,
      enable_lock_screen: orgDefaultData.options.enableLockScreen,
      enable_service_requests: orgDefaultData.options.enableServiceRequest,
      show_user_pin_admin: orgDefaultData.options.enableShowSuperPinAdmin,
      show_user_pin_resident: orgDefaultData.options.enableShowUserPinResident,
      show_user_pin_to_admin: orgDefaultData.options.showUserPinToAdmin,
      enable_add_guests: !!orgDefaultData?.options.enable_add_guests,
      enable_add_co_residents: !!orgDefaultData?.options.enable_add_co_residents,
      allow_resident_credential_issuance: isICTLockProvider(orgDefaultData?.options?.lockProvider)
        ? false
        : !!orgDefaultData?.options.allowResidentCredentialIssuance,
      intercom_name_format: orgDefaultData.options.intercomNameFormat,
      intercom_unit_name_display: orgDefaultData.options.intercomUnitNameDisplay,
      service_request_url: orgDefaultData.serviceRequestURL,
      payment_redirect_url: orgDefaultData.paymentRedirectURL || '',
      org_allow_edits: orgDefaultData.options.enableEdit,
      user_pin_len: DEFAULT_USER_PIN_LENGTH || orgDefaultData.userPinLength,
      enable_thermostats: orgDefaultData.options.enableThermostats,
      enable_leak_detectors: orgDefaultData.options.enableLeakDetectors,
      enable_deliveries: orgDefaultData.options.enableDeliveries,
      enable_chargers: !!orgDefaultData.options.enable_chargers,
      enable_document_storage: !!orgDefaultData.options.enable_document_storage,
      enable_events: !!orgDefaultData.options.enable_events,
      enable_v2_home_page: !!orgDefaultData.options.enable_v2_home_page,
      enable_chat: !!orgDefaultData.options.enableChat,
      enable_akuvox: orgDefaultData.options.enableAkuvox,
      sync_akuvox_co_residents: orgDefaultData.options.syncAkuvoxCoResidents,
      enable_akuvox_private_pins: orgDefaultData.options.enableAkuvoxPrivatePins,
      enable_edit_intercom_pin: !!orgDefaultData.options.enableEditIntercomPin,
      enable_security_cameras: orgDefaultData.options.enableSecurityCameras,
      suite_enabled: orgDefaultData.options.enableMultiLock,
      enable_license_plates: orgDefaultData.options.enableLicensePlates,
      enable_vopay: !!orgDefaultData.options.enableVopay,
      enable_custom_service_request_categories: orgDefaultData.options.enableCustomServiceRequestCategories,
      enable_mfa: !!orgDefaultData.options.enableMfa
    }
  });

  const lockProvider = useWatch({
    control: form.control,
    name: 'lock_provider'
  });

  useEffect(() => {
    if (
      isEdit &&
      organization?.id != null &&
      status === requestSuccess(GET_ORGANIZATION)
    ) {
      form.reset({
        name: organization.name,
        email: organization.email,
        contact: organization.contact,
        logo: null,
        location: organization.config.location,
        lock_provider: organization.config.lock_provider,
        house_id: organization.house_id || '',
        resident_provider: organization.prov_tenant,
        property_provider: organization.prov_property,
        service_provider: organization.prov_service,
        enable_posts: !!organization.config.enable_posts,
        enable_offers: !!organization.config.enable_offers,
        enable_booking: !!organization.config.enable_booking,
        enable_lock_screen: !!organization.config.enable_lock_screen,
        enable_service_requests: !!organization.config.enable_service_requests,
        show_user_pin_admin: !!organization.config.show_user_pin_admin,
        show_user_pin_resident: !!organization.config.show_user_pin_resident,
        show_user_pin_to_admin: !!organization.config.show_user_pin_to_admin,
        enable_add_guests: !!organization?.config.enable_add_guests,
        enable_add_co_residents: !!organization?.config.enable_add_co_residents,
        allow_resident_credential_issuance: isICTLockProvider(organization?.config?.lock_provider)
          ? false
          : !!organization?.config.allow_resident_credential_issuance,
        intercom_name_format: organization.config.intercom_name_format || INTERCOM_NAME_SETTINGS.FULL_NAME.value,
        intercom_unit_name_display: organization.config.intercom_unit_name_display || INTERCOM_UNIT_NAME_DISPLAY_SETTINGS.NONE.value,
        service_request_url: organization.config.service_request_url,
        payment_redirect_url: organization.config.payment_redirect_url || '',
        org_allow_edits: !!organization.config.org_allow_edits,
        user_pin_len: organization.config.user_pin_len,
        enable_thermostats: !!organization.config.enable_thermostats,
        enable_leak_detectors: !!organization.config.enable_leak_detectors,
        enable_deliveries: !!organization.config.enable_deliveries,
        enable_chargers: !!organization.config.enable_chargers,
        enable_document_storage: !!organization.config.enable_document_storage,
        enable_events: !!organization.config.enable_events,
        enable_v2_home_page: !!organization.config.enable_v2_home_page,
        enable_chat: !!organization.config.enable_chat,
        enable_akuvox: !!organization.config.enable_akuvox,
        sync_akuvox_co_residents: !!organization.config.sync_akuvox_co_residents,
        enable_akuvox_private_pins: !!organization.config.enable_akuvox_private_pins,
        enable_edit_intercom_pin: !!organization.config.enable_edit_intercom_pin,
        enable_security_cameras: !!organization.config.enable_security_cameras,
        suite_enabled: !!organization.config.suite_enabled,
        enable_license_plates: !!organization.config.enable_license_plates,
        enable_vopay: !!organization.config.enable_vopay,
        enable_custom_service_request_categories: !!organization.config.enable_custom_service_request_categories,
        enable_mfa: !!organization.config.enable_mfa,
      });
    }
    // Use organization.id, not organization: refetches set a new object for the same org and
    // would reset() the form, clearing dirtyFields while the user is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, organization?.id, status, form]);

  const closeModal = () => {
    dispatch(changeOpenedModal({ modal: '' }));
    dispatch(clearOrgError());
  }

  const watchLogo = form.watch('logo');

  // Destructure formState so RHF’s Proxy subscribes this component to dirty/valid/submitting updates
  const { isSubmitting, isValid, dirtyFields } = form.formState;
  const valuesHaveChanged =
    Object.keys(dirtyFields).length > 0 || Boolean(watchLogo?.fileName) || watchLogo === '';
  const disableSubmit = isSubmitting || !isValid || (isEdit && !valuesHaveChanged);

  const updateOrgSearchTable = (orgId) => {
    dispatch(
      searchTable({
        entity: SEARCH_TABLES.ORGANIZATIONS,
        orgID: orgId,
        body: {
          search: searchState.search,
          minCreationDate: searchState.minCreationDate,
          maxCreationDate: searchState.maxCreationDate,
          filters: searchState.filters,
          ordering: searchState.ordering,
          pagination: searchState.pagination
        },
        success: (res) => {
          dispatch(
            setSearchState({
              currentData: res.data.data ?? [],
              currentPaginationData: res.data.pagination
            })
          );

          if (!res.data.data) {
            dispatch(setUserSearch({ page: 1 }));
            dispatch(
              setSearchPagination({
                page: 1,
                limit: 20
              })
            );
          }
        }
      })
    );
  };

  const addOrEditOrganization = (values, logoUrl = "") => {
    const orgConfig = {
      enable_posts: values.enable_posts,
      enable_offers: values.enable_offers,
      enable_booking: values.enable_booking,
      enable_lock_screen: values.enable_lock_screen,
      enable_service_requests: values.enable_service_requests,
      user_pin_len: values.user_pin_len,
      show_user_pin_resident: values.show_user_pin_resident,
      show_user_pin_admin: values.show_user_pin_admin,
      show_user_pin_to_admin: values.show_user_pin_to_admin,
      enable_add_guests: values.enable_add_guests,
      enable_add_co_residents: values.enable_add_co_residents,
      allow_resident_credential_issuance: isICTLockProvider(values.lock_provider)
        ? false
        : values.allow_resident_credential_issuance,
      service_request_url: values.service_request_url,
      payment_redirect_url: values.payment_redirect_url,
      location: values.location,
      org_allow_edits: values.org_allow_edits,
      lock_provider: values.lock_provider,
      suite_enabled: values.suite_enabled,
      enable_custom_service_request_categories: values.enable_custom_service_request_categories,
      enable_deliveries: values.enable_deliveries,
      enable_chargers: values.enable_chargers,
      enable_document_storage: values.enable_document_storage,
      enable_events: values.enable_events,
      enable_v2_home_page: values.enable_v2_home_page,
      enable_chat: values.enable_chat,
      enable_license_plates: values.enable_license_plates,
      enable_security_cameras: values.enable_security_cameras,
      enable_thermostats: values.enable_thermostats,
      enable_leak_detectors: values.enable_leak_detectors,
      enable_akuvox: values.enable_akuvox,
      sync_akuvox_co_residents: values.sync_akuvox_co_residents,
      enable_akuvox_private_pins: values.enable_akuvox_private_pins,
      enable_edit_intercom_pin: !!(
        values.enable_akuvox &&
        values.enable_akuvox_private_pins &&
        values.enable_edit_intercom_pin
      ),
      enable_vopay: values.enable_vopay,
      intercom_name_format: values.intercom_name_format,
      intercom_unit_name_display: values.intercom_unit_name_display,
      enable_mfa: values.enable_mfa,
    };

    if (isEdit) {
      orgConfig.security_enabled = organization.config.security_enabled;
      orgConfig.enable_global_master_pin = organization.config.enable_global_master_pin;
    }

    const shouldDeleteExistingLogo = isEdit && values.logo === '';

    const orgData = {
      name: values.name.trim(),
      contact: values.contact.trim(),
      email: values.email.trim(),
      prov_tenant: values.resident_provider,
      prov_property: values.property_provider,
      prov_service: values.service_provider,
      ...(isEdit && {
        prov_tenant_mode: organization.prov_tenant_mode,
        prov_property_mode: organization.prov_property_mode,
        prov_service_mode: organization.prov_service_mode,
      }),
      logo: shouldDeleteExistingLogo ? '' : logoUrl || organization.logo || '',
      config: orgConfig
    }

    if (!isEdit) {
      orgData.location = values.location;
      orgData.provider_access_token = values.provider_access_token;
      orgData.provider_refresh_token = values.provider_refresh_token;
    }

    const lockProviderNum = Number(values.lock_provider);
    if (lockProviderNum === LockProviders.YALE || lockProviderNum === LockProviders.ICT_YALE || lockProviderNum === LockProviders.BRIVO_YALE) {
      orgData.house_id = values.house_id;
    }

    if (isEdit) {
      dispatch(
        editOrganization({
          id: organization.id,
          body: orgData,
          success: () => {
            dispatch(
              getOrganizations({
                page: parseInt(pagination.currentPage, 10) || 1
              })
            );
            dispatch(
              getOrganization({
                id: organization.id
              })
            );
            dispatch(getAllOrganizations({ location: orgData?.config?.location || "US" }));
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: `Organization ${orgData.name} has been successfully updated.`
              })
            );
            updateOrgSearchTable(organization.id);
            closeModal();
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to edit organization'),
                modal: 'errorToast'
              })
            );
          }
        })
      );
    } else {
      dispatch(
        addOrganization({
          body: orgData,
          success: () => {
            dispatch(getOrganizations({ location: orgData.location }));
            dispatch(setAllOrgStatus({ status: requestPending(GET_ALL_ORGANIZATIONS) }));
            dispatch(getAllOrganizations({ location: orgData.location }));
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: `Organization ${orgData.name} has been successfully created.`,
              })
            );
            dispatch(resetOrganizationData());
            updateOrgSearchTable(0);
            closeModal();
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to create organization'),
                modal: 'errorToast'
              })
            );
            dispatch(resetOrganizationData());
            updateOrgSearchTable(0);
            closeModal();
          }
        })
      );
    }
  }

  const handleSubmit = (values) => {
    if (values.logo) {
      const imageData = new FormData();
      imageData.append('image', values.logo.blob, values.logo.fileName);

      dispatch(uploadOrgLogo({
        body: imageData,
        success: (res) => {
          addOrEditOrganization(values, res.data.filename);
        },
        fails: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to upload organization logo'),
              modal: 'errorToast'
            })
          );
        }
      }));
    } else {
      addOrEditOrganization(values);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <StyledModalV2 show={modal === 'addOrganization' || modal === 'editOrganization'} side width="550px">
          {status === requestPending(GET_ORGANIZATION) ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : (
            <>
              <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
                <ModalHeader title={isEdit ? t('Edit Organization') : t('Add Organization')} v2 />
                <ModalBodyContent>
                  <OrgInfoFormInputs
                    form={form}
                    lockProvider={lockProvider}
                    existingLogoUrl={isEdit && organization.logo ? organization.logo : ""}
                    isEdit={isEdit}
                  />
                  <CoreFeatureFormInputs form={form} />
                  <UserAccessFormInputs form={form} />
                  <FeatureModuleFormInputs form={form} type={"org"} />
                  <CustomSettingFormInputs form={form} />
                </ModalBodyContent>
              </StyledModalBody>

              <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
                <ButtonBodyVariant type="button" variant="secondary" onClick={closeModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant type="submit" disabled={disableSubmit}>{t(isEdit ? 'Update' : 'Create')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </>
          )}

        </StyledModalV2>
      </form>
    </Form>
  );
};

export default AddEditOrganization;
